import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import BacktestLadder from "./BacktestLadder";
import OverrideControl from "./OverrideControl";
import ForecastChart from "../forecast/ForecastChart";
import { useAllocationSourceSeries } from "./useForecastLab";
import { formatSeriesName } from "./explorer/format";
import {
  visibleLeaderboardRows,
  formatScore,
  isStableScore,
  qualityOf,
  groupByParent,
} from "./leaderboard";

// SourceForecastChart pulls the per-source forecast band-series for an expanded
// allocation row and renders the existing band-ready ForecastChart. Only mounts
// (and therefore only queries) when the row is expanded.
function SourceForecastChart({ sourceKey, expanded }) {
  // Default to 30 days (like the dashboards); the horizon toggle refetches.
  const [horizon, setHorizon] = useState(30);
  const { data: src, isLoading, isError, error } = useAllocationSourceSeries(sourceKey, horizon, expanded);
  if (isLoading) {
    return <div className="px-4 py-4 text-xs text-text-3">Loading source forecast…</div>;
  }
  // Distinguish a request failure (e.g. a BigQuery/auth error on the backend) from
  // a genuinely empty result, so a blank chart is self-explaining.
  if (isError) {
    return (
      <div className="px-4 py-6 text-center text-xs text-danger">
        Couldn&apos;t load this source&apos;s forecast{error?.message ? `: ${error.message}` : ""}. Check the backend logs.
      </div>
    );
  }
  if (!src || !Array.isArray(src.series) || src.series.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-xs text-text-3">
        No forecast series returned for this source.
      </div>
    );
  }
  return (
    <div className="px-4 pb-4">
      {/* No Lab link — we're already in the Lab. The horizon toggle is wired so it
          actually refetches at 30/60/90d. */}
      <ForecastChart
        series={src?.series || []}
        intervalPct={src?.interval_pct ?? 80}
        horizon={horizon}
        onHorizonChange={setHorizon}
        showLabLink={false}
      />
    </div>
  );
}
SourceForecastChart.propTypes = {
  sourceKey: PropTypes.string.isRequired,
  expanded: PropTypes.bool,
};

// fmtSmape formats a sMAPE value (range [0,2]) as a percentage string.
function fmtSmape(smape) {
  if (smape == null) return "—";
  return (smape * 100).toFixed(1) + "%";
}

const METRIC_LABEL = { mase_holdout: "MASE", mase_rolling: "MASE", projection_rolling: "Projection skill" };
const SOURCE_LABEL = { auto: "Auto", override: "Override" };

const TONE_TEXT = { ok: "text-success", attention: "text-warning", critical: "text-danger", neutral: "text-text-3" };
// Quality pills reuse the Financial Control StatusBadge palette for consistency.
const PILL = "inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium";
const QUALITY_PILL = {
  good: "bg-success-soft text-success",
  fair: "bg-warning-soft text-warning",
  poor: "bg-danger-soft text-danger",
  unreliable: "bg-danger-soft text-danger",
  unrated: "bg-surface-2 text-text-2",
};

const QUALITY_OPTIONS = [
  { value: "all", label: "All quality" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
  { value: "unreliable", label: "Unreliable" },
];

const NCOLS = 7;

// A row "changes" on promote when its auto-picked winner differs from the live
// model (or there is no live model yet).
function isPending(row) {
  return !!(row.winner && row.source !== "override" && row.winner.algorithm !== row.live);
}

// SortHeader is a clickable column header that toggles the active sort. The hint
// ⓘ is a sibling of the sort button (not nested inside it) so hovering it shows
// the tooltip without also triggering a sort.
function SortHeader({ label, col, sort, onSort, align = "left", hint }) {
  const active = sort.key === col;
  return (
    <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`flex items-center gap-1 text-xs font-medium uppercase tracking-wide transition-colors ${
          active ? "text-primary" : "text-text-3 hover:text-text-2"
        }`}
      >
        {label}
        {active && <span aria-hidden="true">{sort.dir === "asc" ? "↑" : "↓"}</span>}
      </button>
      {hint && (
        <span
          className="cursor-help text-text-3"
          data-tooltip-id="instantTooltip"
          data-tooltip-content={hint}
          aria-label={hint}
        >
          ⓘ
        </span>
      )}
    </div>
  );
}
SortHeader.propTypes = {
  label: PropTypes.string,
  col: PropTypes.string,
  sort: PropTypes.object,
  onSort: PropTypes.func,
  align: PropTypes.string,
  hint: PropTypes.string,
};

export default function LeaderboardTable({ rows, loading, onOverride, scope, nameMap }) {
  // Prefer a backend-resolved display name (e.g. project name) over the formatted key.
  const labelOf = (series) => nameMap[series] || formatSeriesName(series).label;
  const [ladder, setLadder] = useState(null); // series whose backtest ladder is open
  const [openGroups, setOpenGroups] = useState({}); // parent key -> children expanded
  const [text, setText] = useState("");
  const [source, setSource] = useState("all");
  const [quality, setQuality] = useState("all");
  const [sort, setSort] = useState({ key: "series", dir: "asc" });

  const isAllocation = scope === "allocation";
  const firstColLabel = isAllocation ? "Source" : "Series";

  const visible = useMemo(() => visibleLeaderboardRows(rows), [rows]);
  const metricLabel =
    METRIC_LABEL[visible.find((r) => r.winner?.metric)?.winner?.metric] || "Score";

  function toggleSort(key) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  function matches(row) {
    if (!row) return false;
    const q = text.trim().toLowerCase();
    const label = labelOf(row.series).toLowerCase();
    const nameOk = !q || label.includes(q) || String(row.series).toLowerCase().includes(q);
    const srcOk = source === "all" || row.source === source;
    const qualOk = quality === "all" || qualityOf(row).level === quality;
    return nameOk && srcOk && qualOk;
  }

  function sortValue(row) {
    if (!row || !row.winner) return Number.POSITIVE_INFINITY; // sink no-data
    if (sort.key === "smape") return row.winner.smape ?? Number.POSITIVE_INFINITY;
    if (sort.key === "skill") {
      const s = row.winner.score_out_of_sample ?? row.winner.score;
      return isStableScore(s) ? s : Number.POSITIVE_INFINITY;
    }
    return null;
  }

  function cmp(a, b) {
    const mul = sort.dir === "asc" ? 1 : -1;
    if (sort.key === "series") return mul * String(a?.series).localeCompare(String(b?.series));
    return mul * (sortValue(a) - sortValue(b));
  }

  const groups = useMemo(() => {
    const built = groupByParent(visible);
    const kept = [];
    for (const g of built) {
      const childMatch = g.children.filter(matches);
      const parentMatch = g.parent ? matches(g.parent) : false;
      if (!parentMatch && childMatch.length === 0) continue;
      kept.push({
        ...g,
        children: [...g.children].sort(cmp),
        forceOpen: childMatch.length > 0 && !parentMatch,
      });
    }
    kept.sort((a, b) => cmp(a.parent || { series: a.key }, b.parent || { series: b.key }));
    return kept;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, text, source, quality, sort]);

  if (loading) {
    return (
      <div className="rounded-card border border-card-bd bg-card px-4 py-8 text-center text-sm text-text-3">
        Loading…
      </div>
    );
  }

  if (!visible || visible.length === 0) {
    return (
      <div className="rounded-card border border-card-bd bg-card px-4 py-12 text-center">
        <p className="text-sm text-text-3">No competition results yet.</p>
        <p className="mt-1 text-xs text-text-3">Run a competition to see per-series model rankings.</p>
      </div>
    );
  }

  function renderRow(row, { isChild, group } = {}) {
    const open = ladder === row.series;
    const q = qualityOf(row);
    const oos = row.winner?.score_out_of_sample;
    const ins = row.winner?.score;
    const primary = oos != null ? oos : ins;
    const showInSample =
      oos != null && ins != null && isStableScore(oos) && isStableScore(ins) && Math.abs(oos - ins) >= 0.05;
    const overfit = showInSample && ins < oos - 0.05;
    const runnerUpLabel = row.runnerUp ? `${row.runnerUp.algorithm} (${fmtSmape(row.runnerUp.smape)})` : "—";
    const sn = formatSeriesName(row.series);
    const displayName = isChild
      ? sn.label.split(" · ").slice(1).join(" · ") || sn.label
      : nameMap[row.series] || sn.label;

    return (
      <React.Fragment key={row.series}>
        <tr
          className={`group cursor-pointer border-b border-card-bd transition-colors hover:bg-surface-2 ${
            open ? "bg-surface-2" : ""
          }`}
          onClick={() => setLadder((p) => (p === row.series ? null : row.series))}
          aria-expanded={open}
        >
          {/* Series — wide enough to read full project/source names */}
          <td className={`px-4 py-2.5 ${isChild ? "pl-10" : ""}`} style={{ minWidth: 280 }}>
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-3 shrink-0 text-text-3 transition-transform"
                style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
                aria-hidden="true"
              >
                ›
              </span>
              <span
                className="font-medium text-text"
                {...(sn.hint ? { "data-tooltip-id": "instantTooltip", "data-tooltip-content": sn.hint } : {})}
              >
                {displayName}
              </span>
              {group && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenGroups((p) => ({ ...p, [group.key]: !group.open }));
                  }}
                  aria-expanded={group.open}
                  aria-label={`${group.open ? "Hide" : "Show"} ${group.count} per-unit series`}
                  className="inline-flex shrink-0 items-center gap-0.5 rounded-ctrl border border-card-bd px-1.5 py-0.5 text-[11px] font-medium text-text-3 hover:border-border-strong hover:text-text-2"
                >
                  <span aria-hidden="true">{group.open ? "▾" : "▸"}</span>
                  {group.count} unit{group.count === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </td>

          {/* Winning model */}
          <td className="px-4 py-2.5">
            {row.winner ? (
              <span className={`${PILL} bg-success-soft font-semibold text-success`}>{row.winner.algorithm}</span>
            ) : (
              <span className="text-text-3">—</span>
            )}
          </td>

          {/* Quality */}
          <td className="px-4 py-2.5">
            {q.level === "none" ? (
              <span className="text-text-3">—</span>
            ) : (
              <span className={`${PILL} ${QUALITY_PILL[q.level]}`}>{q.label}</span>
            )}
          </td>

          {/* Selection score */}
          <td className="px-4 py-2.5 text-right tabular-nums text-text-2">
            {row.winner ? (
              <>
                <span title={oos != null ? "Honest out-of-sample score" : "In-sample score"}>
                  {formatScore(primary)}
                </span>
                {showInSample && (
                  <span
                    className={`ml-1 text-xs ${overfit ? "text-warning" : "text-text-3"}`}
                    data-tooltip-id="instantTooltip"
                    data-tooltip-content={
                      overfit
                        ? "In-sample looks better than out-of-sample — possible overfit."
                        : "In-sample score"
                    }
                  >
                    in-sample: {formatScore(ins)}
                  </span>
                )}
              </>
            ) : (
              "—"
            )}
          </td>

          {/* sMAPE — coloured by quality */}
          <td className={`px-4 py-2.5 text-right tabular-nums ${TONE_TEXT[q.tone]}`}>
            {row.winner ? fmtSmape(row.winner.smape) : "—"}
            {row.winner?.pinball != null && (
              <span
                className="ml-2 text-xs text-text-3"
                title="Pinball (quantile) loss — lower = better-calibrated intervals"
              >
                pb {row.winner.pinball.toFixed(2)}
              </span>
            )}
          </td>

          {/* Runner-up */}
          <td className="px-4 py-2.5 text-text-3">{runnerUpLabel}</td>

          {/* Selected by + pending marker + hover-revealed override */}
          <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <span
                className={`${PILL} ${
                  row.source === "override" ? "bg-primary-soft text-primary" : "bg-surface-2 text-text-2"
                }`}
              >
                {SOURCE_LABEL[row.source] || row.source}
              </span>
              {isPending(row) && (
                <span
                  className={`${PILL} bg-warning-soft text-warning`}
                  data-tooltip-id="instantTooltip"
                  data-tooltip-content={`Promote will change the live model${row.live ? ` from ${row.live}` : ""} to ${row.winner.algorithm}.`}
                >
                  pending
                </span>
              )}
              {onOverride && (
                <div
                  className={`ml-auto transition-opacity ${
                    row.source === "override" ? "" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
                  }`}
                >
                  <OverrideControl
                    series={row.series}
                    current={row.source === "override" ? (row.winner?.algorithm ?? "") : ""}
                    onOverride={onOverride}
                  />
                </div>
              )}
            </div>
          </td>
        </tr>

        {open && (
          <tr className="border-b border-card-bd bg-surface-2">
            <td colSpan={NCOLS} className="p-0">
              <BacktestLadder ladder={row.ladder} />
              {isAllocation && <SourceForecastChart sourceKey={row.series} expanded={open} />}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  return (
    <div className="rounded-card border border-card-bd bg-card">
      {/* Filter bar — the card's header strip */}
      <div className="flex flex-wrap items-center gap-2 border-b border-card-bd px-4 py-2.5">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Filter ${firstColLabel.toLowerCase()}…`}
          aria-label={`Filter ${firstColLabel.toLowerCase()}`}
          className="w-48 rounded-ctrl border border-card-bd px-2.5 py-1 text-sm text-text-2 placeholder:text-text-3 focus:border-primary focus:outline-none"
        />
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          aria-label="Filter by source"
          className="rounded-ctrl border border-card-bd px-2 py-1 text-sm text-text-2 focus:border-primary focus:outline-none"
        >
          <option value="all">All sources</option>
          <option value="auto">Auto</option>
          <option value="override">Override</option>
        </select>
        <select
          value={quality}
          onChange={(e) => setQuality(e.target.value)}
          aria-label="Filter by quality"
          className="rounded-ctrl border border-card-bd px-2 py-1 text-sm text-text-2 focus:border-primary focus:outline-none"
        >
          {QUALITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-text-3">
          {groups.length} {isAllocation ? "source" : "resource"}
          {groups.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="border-b border-card-bd text-left">
              <th className="px-4 py-2.5" style={{ minWidth: 280 }}>
                <SortHeader label={firstColLabel} col="series" sort={sort} onSort={toggleSort} />
              </th>
              <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-text-3">Winning model</th>
              <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-text-3">Quality</th>
              <th className="px-4 py-2.5 text-right">
                <SortHeader
                  label={metricLabel}
                  col="skill"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                  hint="Rolling-origin selection score vs a naive baseline — lower is better. “—” when there isn’t enough signal to score reliably."
                />
              </th>
              <th className="px-4 py-2.5 text-right">
                <SortHeader
                  label="sMAPE"
                  col="smape"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                  hint="Average % forecast error on the holdout — lower is better."
                />
              </th>
              <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-text-3">Runner-up</th>
              <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-text-3">Selected by</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr>
                <td colSpan={NCOLS} className="px-4 py-10 text-center text-sm text-text-3">
                  No series match the current filters.
                </td>
              </tr>
            )}
            {groups.map((g) => {
              const hasChildren = g.children.length > 0;
              const groupOpen = g.forceOpen || !!openGroups[g.key];
              const parentRow =
                g.parent || { series: g.key, winner: null, runnerUp: null, ladder: [], source: "auto", live: null };
              return (
                <React.Fragment key={g.key}>
                  {renderRow(parentRow, {
                    group: hasChildren ? { count: g.children.length, open: groupOpen, key: g.key } : null,
                  })}
                  {hasChildren && groupOpen && g.children.map((c) => renderRow(c, { isChild: true }))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

LeaderboardTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      series: PropTypes.string.isRequired,
      winner: PropTypes.shape({
        algorithm: PropTypes.string.isRequired,
        smape: PropTypes.number,
        score: PropTypes.number,
        score_sd: PropTypes.number,
        score_out_of_sample: PropTypes.number,
        metric: PropTypes.string,
        pinball: PropTypes.number,
      }),
      runnerUp: PropTypes.shape({
        algorithm: PropTypes.string.isRequired,
        smape: PropTypes.number,
      }),
      ladder: PropTypes.array.isRequired,
      source: PropTypes.string.isRequired,
      live: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  /** When provided, renders an OverrideControl in the Selected-by column on hover. */
  onOverride: PropTypes.func,
  /** "allocation" relabels the first column to "Source" and adds a per-source
   *  band drill-down chart under each expanded row. */
  scope: PropTypes.string,
  /** Optional series-key → display-name map (e.g. project names) preferred over
   *  the formatted key. */
  nameMap: PropTypes.object,
};

LeaderboardTable.defaultProps = {
  rows: [],
  loading: false,
  onOverride: null,
  scope: "insights",
  nameMap: {},
};
